// Build notes for the workflow pages: what is done and what is still to build, per step. For the
// Monada team only: shown to the super-admin in a collapsed "Build notes" section at the bottom
// of a workflow page, never to customers. The customer-facing step texts are in elariomData.js;
// the full design and log are in docs/air-agent/platform-plan.md.

export const BUILD_NOTES = {
    'inquiry-to-accepted-quote': {
        steps: {
            email: {
                done: ['Monada already reads users\' Outlook and Gmail mailboxes for RFQ replies.'],
                pending: ['Read every incoming email, not only RFQ replies.', 'Connect the company inbox as well as personal mailboxes.'],
            },
            chat: {
                done: [
                    'One Elariom per workspace: a Teams user people add as an external contact (DEMO: elariom.fastcargo@monada.cloud), connected from Connectors.',
                    'It answers only the workspace\'s users: recognised by company email, or confirmed once with an emailed code.',
                    '"Runs on" per workspace (Production / ALPHA / Local): one server answers, chosen on Connectors.',
                    'Typing line and progress status; failures said in the chat; time limits so the loop never freezes.',
                    'Running on ALPHA (ELARIOM_ENV=alpha, settings in ~/release.sh). Every conversation is recorded under Executions.',
                    'Ask Elariom: the same Elariom as a chat page in Elariom.',
                ],
                pending: ['ALPHA\'s 8 GB disk breaks deploys: resize to 30 GB. DEMO runs on Local until ALPHA is healthy again.', 'Microsoft pushing new messages instead of checking every 5 seconds.', 'The rest of Monada\'s data: quotes, emails, the job.', 'Elariom\'s own mailbox for forwarded inquiries.', 'Connect: refuse any account that isn\'t an elariom.…@monada.cloud address.'],
            },
            triage: {
                pending: ['The classifier.', 'Linking to a job: by what Monada sent, then by references (quote, booking, BL, container numbers), then by content, asking when unsure.', 'Priority: waiting time, urgency, customer importance, how concrete the inquiry is.'],
            },
            others: {
                done: ['Parse supplier rate files exists and takes files and emails.'],
                pending: ['The hand-over itself, from the triage step.'],
            },
            job: {
                pending: ['Decide what a job is in Monada\'s data, next to quotes, bookings and shipments.'],
            },
            intake: {
                done: ['The quote holds route, products and cargo.', 'In Teams, Elariom asks for what a price question is missing, one question at a time: mode (when not obvious), equipment and anything special, date. Not routing or carriers.'],
                pending: ['Reading a customer\'s inquiry email into a quote.', 'The full completeness rules per mode and cargo (MSDS for DG, temperature for reefer, …).', '"Same as last time".'],
            },
            ready: {
                pending: ['The pricing-plan rules (to be developed).'],
            },
            ask: {
                pending: ['Drafting the questions to the customer.', 'Approval in Teams.', 'Waiting and continuing when the answer arrives.'],
            },
            search: {
                done: [
                    'Rate search in the quote sidebar (Monada rates, Okargo, CargoFive, CargoAI), used by hand.',
                    'Elariom searches the workspace\'s own rates first and sends them right away, then Okargo; the latest expired rates as the last known price; filters by routing and carrier when asked.',
                    'Countries: "China to Malta" searches every Monada sea port in the country (about 5 seconds).',
                ],
                pending: ['Searching per leg, from the pricing plan.', 'CargoFive and carrier websites where Okargo doesn\'t cover the equipment.', 'Spot offers searchable across quotes.', 'Recent RFQ replies as provisional options.'],
            },
            sources: {
                done: ['Connectors for Okargo, CargoFive, CargoAI and Monada\'s own rates.', 'Elariom searches the workspace\'s rates and Okargo, live, from Teams.'],
                pending: ['Elariom searching CargoFive too.', 'Choosing the source per request from each provider\'s coverage (rate-sources-coverage.md).'],
            },
            portals: {
                pending: ['Use the carrier logins the workspace already keeps (Okargo platforms and portals in Settings), encrypted at rest.', 'The hosted browser.', 'Portal recipes, one carrier at a time.', 'Asking the user in Teams for one-time codes; never getting around a CAPTCHA.', 'Checking each carrier\'s terms for automated access and its daily limits.'],
            },
            rfq: {
                done: ['RFQs sent from the quote, replies tracked, prices read from them by AI.'],
                pending: ['Choosing who to ask (coverage, scores, promises of work).', 'Drafting RFQs from the job.', 'Following up by itself.', 'Finding someone new where there is no supplier.'],
            },
            suppliers: {
                done: ['Supplier records in Monada; RFQs by email.'],
                pending: ['Who covers which country and service; scoring.', 'WhatsApp.'],
            },
            collector: {
                done: ['Cost center and offers on the quote; margin per field.', 'In Teams: search prices are costs, never shown to a customer; Elariom asks for the margin, calculates sell prices in code (price_with_margin), shows cost / margin / sell, then drafts the customer reply with sell prices only and no internal labels.', 'Converts to one currency with the workspace\'s own exchange rates, else the live rate, saying which (convert_currency).'],
                pending: ['Combining legs into options.', 'The comparison as a table in Teams.', 'The company\'s margin policy, so it suggests instead of asking.', 'Creating the quote in Monada from the chosen option.'],
            },
            approve: {
                done: ['Quote builders, PDF and sending from Monada.'],
                pending: ['Sending from Monada after approval in Teams (today Elariom only drafts the text).', 'Who approves: pricing (costs, margin) or the sales person (what goes out).'],
            },
            teams: {
                done: ['One Elariom per workspace, chatting end to end: asks for missing details, looks up rates and Okargo, applies margin, drafts the customer reply.'],
                pending: ['Tables and buttons for comparisons and approvals.', 'Paragraph spacing: Teams shows either none or a full blank line.'],
            },
            followup: {
                done: ['The follow-up queue, by hand.'],
                pending: ['Watching the thread by itself.', 'Recognising acceptance, rejection, and questions that go back to Approve & send.'],
            },
            accepted: {
                pending: ['Handing the job to Booking to voyage completion with everything agreed.'],
            },
        },
        next: [
            'Resize ALPHA\'s disk, deploy the latest, and move DEMO\'s Elariom back to Runs on → ALPHA.',
            'Connect: accept only elariom.…@monada.cloud accounts.',
            'Carrier websites: a browser agent, starting with one carrier.',
            'Triage on one mailbox: classify and link to a job, shown in Teams.',
            'Understand an inquiry into a quote, with the missing-information questions.',
        ],
    },
    'rfq-to-best-offer': {
        steps: {
            request: { pending: ['Reading the mailbox for requests and POs (needs the mailbox triage of Inquiry to accepted quote).'] },
            chat: { done: ['Elariom in Teams takes requests in chat.'], pending: ['"Get me rates for…" starting this workflow.'] },
            fromQuote: { pending: ['The pricing plan of a quote handing its unpriced legs here.'] },
            understand: {
                done: ['The quote holds route, products, cargo; Elariom asks for missing price details in Teams.', 'The rate agent already reads lanes, Incoterms and door addresses from files (case 05).'],
                pending: ['Reading a request or PO into lanes and legs, with Incoterm per lane and the deadline.', 'Door / ZIP destinations in the rate model (customer request).'],
            },
            complete: { pending: ['Completeness rules per mode and cargo, shared with the quote workflow.'] },
            askRequester: { pending: ['Asking the requester in Teams, one question at a time, and continuing on the answer.'] },
            choose: { pending: ['Supplier coverage per country and service.', 'Scores from RFQ history: response rate and time, win rate, price against others.', 'A "promised work" flag that outranks price.'] },
            scores: { done: ['Supplier records in Monada.'], pending: ['Coverage and tagging (lanes, services) — supplier onboarding.', 'Scores computed from RFQ history.'] },
            discover: { pending: ['Networks and directories.', 'WhatsApp groups (approved, no customer or prices revealed).'] },
            draft: {
                done: ['RFQs are sent from the quote today.'],
                pending: ['Drafting one RFQ per supplier from the request: complete, with urgency and deadline, customer rephrased away, in the supplier\'s language.', 'Reference in the subject for linking replies (Monada quote numbers already do this).'],
            },
            send: { done: ['RFQ send from the quote, tracked per supplier.'], pending: ['Approval of suppliers and RFQs in Teams.'] },
            suppliers: { done: ['RFQs by email.'], pending: ['WhatsApp.'] },
            replies: {
                done: ['RFQ replies are read by AI on the quote.', 'The rate agent reads email bodies and PDFs; case 05 (JTM / Cotesi) lists what replies teach: newest message only, NAC, DAP vs CFR per lane, "Included", per-BL charges, mixed currencies, % charges, several options per lane, totals as a check.'],
                pending: ['Applying those rules to RFQ replies.', 'Supplier and carrier as separate fields (customer request).', 'Named-account rates (Tanko, JTM).'],
            },
            followup: { pending: ['Reminders before the deadline; asking partial repliers for the missing lanes; suggesting an alternative supplier.'] },
            coverage: { pending: ['The lanes × offers table, kept up to date as replies arrive.'] },
            compare: {
                done: ['Elariom compares options, converts to one currency with the workspace\'s rates and applies margin (Teams, Ask Elariom).'],
                pending: ['Options built from RFQ replies; reliability from the scores; the comparison as a table.'],
            },
            provisional: { pending: ['Recent RFQ replies searchable across quotes and shown as provisional.'] },
            decide: { pending: ['Choosing per lane in Teams.'] },
            close: {
                done: ['Offers saved on the quote; rates can be published.'],
                pending: ['Telling suppliers won or lost, and why.', 'Publishing tender or contract replies as (account-scoped) rates.'],
            },
            quote: { pending: ['Handing chosen offers back to Collect & compare.'] },
        },
        next: [
            'The demo ("Run the demo") already does the reading, choosing, drafting and comparing with Claude, on made-up suppliers and replies. Making it real starts here:',
            'Supplier coverage and tagging (who covers which country and service): the base for choosing who to ask.',
            'Drafting RFQs from a request in Teams, approved and sent from Monada.',
            'Reading RFQ replies with the case 05 rules into comparable options, with the lanes × offers table.',
            'Automatic follow-ups before the deadline.',
        ],
    },
    'booking-to-voyage-completion': {
        steps: {
            accepted: {
                pending: ['Starting from the acceptance email, or a quote marked won in Monada.'],
            },
            booking: {
                done: ['Bookings page; won quotes sent to CargoWise.'],
                pending: ['Asking the customer for the missing booking details.', 'Preparing the handoff to operations by itself.'],
            },
            cargowise: {
                done: ['CargoWise connector.'],
            },
            coordinate: {
                pending: ['All of it: passing contacts between customer, agent, trucker and warehouse on the right threads.'],
            },
            teams: {
                pending: ['Booking questions and approvals in the Teams chat.'],
            },
            arrival: {
                pending: ['All of it: notice of arrival, updates, closing the job.'],
            },
            job: {
                pending: ['Decide what a job is in Monada\'s data; carry it over from the quote.'],
            },
        },
        next: ['Design after Inquiry to accepted quote works end to end.'],
    },
};
